import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useRouter } from 'next/navigation';
import qs from 'qs';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import useForm, { validation } from '@calm-web/use-form';

import { useAnalytics } from '@/hooks/analytics/useAnalytics';
import { DiscountedPricingDetails } from '@/hooks/api/useDiscountedPricing';
import { InvoiceResponse, useInvoicePreview } from '@/hooks/api/useInvoicePreview';
import { usePurchase } from '@/hooks/api/usePurchase';
import { TEAMS_USER_INFO_KEY } from '@/hooks/useStoreSsCheckoutProgress';
import { setBannerMessage } from '@/store/actions';
import { Partner } from '@/types/store/reducers';
import { formatError } from '@/utils/analytics/dataFormatting';
import { CalmError } from '@/utils/apiRequest/errors';
import { calmLogger } from '@/utils/calmLogger';

import { CompanyFormFields, InfoFormFields, TeamsPageName } from '../../types';
import { calculatingSelectedContractedLives } from '../../utils';
import messages from './messages';
import PaymentOverview from './PaymentOverview';
import PaymentTermsAndConditions from './PaymentTermsAndConditions';
import tocMessages from './PaymentTermsAndConditions/messages';
import StripePaymentElementForm from './StripePaymentElementForm';
import { Container, Form, FormContainer, SubmitButton } from './styles';

interface PaymentFormProps {
	selectedCoveredLives: number;
	infoPageFields: InfoFormFields;
	companyPageFields: CompanyFormFields;
	setActivePage: (page: TeamsPageName) => Promise<void>;
	discountedPricing: DiscountedPricingDetails;
	coupon?: string;
}

const LOCATION = 'payment';

const PaymentForm: React.FC<PaymentFormProps> = ({
	selectedCoveredLives,
	infoPageFields,
	companyPageFields,
	setActivePage,
	coupon,
	discountedPricing,
}) => {
	const [isPurchasing, setIsPurchasing] = useState(false);
	const [shouldShowTaxAmount, setShouldShowTaxAmount] = useState(false);
	const [isLoadingTaxes, setIsLoadingTaxes] = useState(false);
	const [invoiceTotals, setInvoiceTotals] = useState<InvoiceResponse>();
	const router = useRouter();
	const { formatMessage } = useIntl();
	const { submitSelfServePurchase, handlePurchaseError } = usePurchase();
	const { logEvent } = useAnalytics();
	const dispatch = useDispatch();
	const stripe = useStripe();
	const elements = useElements();
	const [getInvoiceData, { loading: isInvoicePreviewLoading, error: invoicePreviewError }] =
		useInvoicePreview();
	const formProps = useForm('selfServePayment', {
		initialModel: {
			isTocChecked: [],
		},
		validation: {
			isTocChecked: validation.validateOrFail([
				{
					rules: [validation.required],
					errorResult: formatMessage(tocMessages.error),
				},
			]),
		},
	});

	const fullName = infoPageFields ? `${infoPageFields.firstName} ${infoPageFields.lastName}` : '';

	// Use confirmation token to get invoice data from the server
	const getInvoicePreview = useCallback(
		async (selectedCoveredLives: number, confirmationTokenId: string, coupon?: string): Promise<void> => {
			try {
				const response = await getInvoiceData(selectedCoveredLives, confirmationTokenId, coupon);
				setInvoiceTotals(response);
			} catch (err) {
				setShouldShowTaxAmount(false);
				dispatch(
					setBannerMessage({
						message: 'Failed to retrieve tax details',
						isError: true,
						flash: true,
					}),
				);
			}
		},
		[getInvoiceData, dispatch],
	);

	const calculateTaxValues = async (): Promise<void> => {
		if (!stripe) {
			// if !stripe, Stripe.js hasn't loaded yet
			return;
		}
		if (!elements) {
			// if !elements, Stripe.js hasn't loaded yet
			return;
		}

		const { error: submitError } = await elements.submit();
		if (submitError) {
			dispatch(
				setBannerMessage({
					message: 'Failed to retrieve tax details',
					isError: true,
					flash: true,
				}),
			);
			return;
		}

		setShouldShowTaxAmount(true);

		// Get confirmation token ID from stripe once all fields are filled out
		try {
			const { confirmationToken } = await stripe.createConfirmationToken({
				elements,
			});
			if (confirmationToken && confirmationToken.id) {
				await getInvoicePreview(selectedCoveredLives, confirmationToken.id, coupon);
			}
		} catch (err) {
			setShouldShowTaxAmount(false);
			dispatch(
				setBannerMessage({
					message: 'Failed to retrieve tax details',
					isError: true,
					flash: true,
				}),
			);
		} finally {
			setIsLoadingTaxes(false);
		}
	};

	const handleSuccessfulPurchase = async ({
		partner,
		redirectUrl,
		paymentMethodType,
		coupon,
	}: {
		partner: Partner;
		redirectUrl?: string;
		paymentMethodType: string | undefined;
		coupon?: string;
	}): Promise<void> => {
		const email = infoPageFields?.email || '';
		const firstName = infoPageFields?.firstName;

		logEvent('Teams : Checkout : Completed', {
			id: partner.id,
			name: partner.name,
			email,
			companySize: companyPageFields.companySize ?? '',
			selectedCoveredLives: selectedCoveredLives,
			selectedContractedLives: calculatingSelectedContractedLives(selectedCoveredLives),
			location: LOCATION,
			variant: '7-16-24',
			...(paymentMethodType ? { payment_method_type: paymentMethodType } : {}),
			coupon: coupon || '',
		});

		sessionStorage.removeItem(TEAMS_USER_INFO_KEY);

		const successPathQuery = {
			redirectUrl: encodeURI(redirectUrl as string),
			...(firstName ? { firstName } : {}),
			...(email ? { email } : {}),
		};

		await router.push(`/teams/success?${qs.stringify(successPathQuery)}`);
	};

	const onPurchase = async (e: SyntheticEvent): Promise<void> => {
		e.preventDefault();
		formProps.setAllDirtyStatesBlurred();
		let paymentMethodType: string | undefined;
		try {
			setIsPurchasing(true);

			if (!stripe) {
				// if !stripe, Stripe.js hasn't loaded yet
				return;
			}
			if (!elements) {
				throw new Error('Stripe Elements provider not found', {
					cause: 'Stripe Elements provider not found in any parent component',
				});
			}

			if (!formProps.validation.isValid) {
				// formProps.setAllDirtyStatesBlurred above will show error messages as appropriate
				return;
			}

			const { error: submitError } = await elements.submit();
			if (submitError) {
				throw new Error('Stripe elements.submit() error', { cause: JSON.stringify(submitError) });
			}

			// Create the ConfirmationToken using the details collected by the Payment Element
			const { error, confirmationToken } = await stripe.createConfirmationToken({
				elements,
			});
			if (error) {
				throw new Error('stripe.createConfirmationToken error', { cause: JSON.stringify(error) });
			}

			const { email, firstName, lastName } = infoPageFields;
			const { companyName, companySize, role } = companyPageFields;
			paymentMethodType = confirmationToken.payment_method_preview.type;

			logEvent('Teams : Checkout : Posted', {
				email,
				selectedCoveredLives,
				selectedContractedLives: calculatingSelectedContractedLives(selectedCoveredLives),
				companySize: companySize ?? '',
				location: LOCATION,
				variant: '7-16-24',
				coupon: coupon || '',
				...(paymentMethodType ? { payment_method_type: paymentMethodType } : {}),
			});
			const { data } = await submitSelfServePurchase({
				email,
				first_name: firstName,
				last_name: lastName,
				company_name: companyName,
				company_size: companySize,
				title: role,
				covered_lives: selectedCoveredLives,
				stripe_confirmation_token_id: confirmationToken.id,
				coupon: coupon,
			});
			await handleSuccessfulPurchase({
				partner: data.partner,
				redirectUrl: data.redirectUrl,
				paymentMethodType,
				coupon: coupon,
			});
		} catch (err) {
			logEvent('Teams : Checkout : Error', {
				...formatError(err),
				email: infoPageFields?.email || '',
				selectedCoveredLives: selectedCoveredLives,
				selectedContractedLives: calculatingSelectedContractedLives(selectedCoveredLives),
				companySize: companyPageFields.companySize ?? '',
				location: LOCATION,
				variant: '7-16-24',
				...(paymentMethodType ? { payment_method_type: paymentMethodType } : {}),
				coupon: coupon || '',
			});
			const errorMessage = handlePurchaseError(err as Error | CalmError | string);
			dispatch(
				setBannerMessage({
					message: errorMessage,
					isError: true,
					flash: true,
				}),
			);
			calmLogger.error('Teams : Checkout : Error', { errorMessage }, err);
		} finally {
			setIsPurchasing(false);
		}
	};

	const context = {
		location: LOCATION,
		companySize: companyPageFields?.companySize ?? '',
		selectedCoveredLives,
		variant: '7-16-24',
	};

	const finalPricing = useMemo(() => {
		const subtotal = invoiceTotals?.subtotal
			? invoiceTotals.subtotal
			: discountedPricing.originalTieredPrice!;
		const tax = invoiceTotals?.tax_amount;
		const discount = invoiceTotals?.discount_amount
			? invoiceTotals.discount_amount
			: discountedPricing.originalTieredPrice! - discountedPricing.totalCost!;

		const totalDue = invoiceTotals?.total ? invoiceTotals.total : discountedPricing.totalCost!;
		const couponDuration = discountedPricing.coupon?.duration || '';

		return {
			subtotal,
			tax,
			discount,
			totalDue,
			couponDuration,
		};
	}, [
		discountedPricing.coupon,
		discountedPricing.originalTieredPrice,
		discountedPricing.totalCost,
		invoiceTotals?.discount_amount,
		invoiceTotals?.subtotal,
		invoiceTotals?.tax_amount,
		invoiceTotals?.total,
	]);

	useEffect(() => {
		logEvent('Teams : Checkout : Landed', {
			email: infoPageFields?.email || '',
			selectedCoveredLives,
			selectedContractedLives: calculatingSelectedContractedLives(selectedCoveredLives),
			companySize: companyPageFields?.companySize ?? '',
			location: LOCATION,
			variant: '7-16-24',
			coupon: coupon || '',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Container>
			<Form onSubmit={onPurchase}>
				<FormContainer>
					<StripePaymentElementForm
						fullName={fullName}
						context={context}
						calculateTaxValues={calculateTaxValues}
						setIsLoadingTaxes={setIsLoadingTaxes}
						setShouldShowTaxAmount={setShouldShowTaxAmount}
					/>
					<PaymentOverview
						finalPricing={finalPricing}
						selectedCoveredLives={selectedCoveredLives}
						setActivePage={setActivePage}
						isLoadingTaxes={isLoadingTaxes || isInvoicePreviewLoading}
						shouldShowTaxAmount={shouldShowTaxAmount}
						context={context}
					/>
				</FormContainer>
				<PaymentTermsAndConditions formProps={formProps} context={context} />
				<SubmitButton
					isDisabled={isPurchasing || isLoadingTaxes || isInvoicePreviewLoading || !!invoicePreviewError}
					isLoading={isPurchasing}
					data-testid="checkout-button"
				>
					{formatMessage(messages.submitButton)}
				</SubmitButton>
			</Form>
		</Container>
	);
};

export default PaymentForm;
